import * as THREE from "three";
import { ElementType } from "../data-structures/element-type";
import { MeasurementType } from "../data-structures/measure-type";
import { Utils } from "../utils/utils";
import { radToDeg } from "three/src/math/MathUtils";

declare const proj4: any;
declare const Potree: any;
const TextSprite = Potree?.TextSprite;

export class UnifiedLineMeasure extends THREE.Object3D {    
    sphereGeometry = new THREE.SphereGeometry(0.7, 10, 10);
    layerName = "";
    points: { position: THREE.Vector3 }[] = [];
    hasMapCoordinates = false;
    unit = "m";
    projectCoordinateSystem: string | null = null;
    closed = false;
    name = "Measurement";
    mapCoordinateSystem = "EPSG:3857";
    
    private _color = new THREE.Color(0xffffff);
    private _selected = false;
    private _measurementTypes: MeasurementType[] = [];
    private _showDistances = true;
    private _showCoordinates = false;
    
    protected spheres: THREE.Object3D[] = [];
    protected edgeLabels: any[] = [];
    protected lines: THREE.Line[] = [];
    
    constructor() {
        super();
    }

    get color(): THREE.Color {
        return this._color;
    }
    
    set color(value: THREE.Color) {
        this._color.copy(value);
        
        // Update colors on existing objects
        this.lines.forEach(line => {
            if (line.material instanceof THREE.LineBasicMaterial) {
                line.material.color = this._color;
            }
        });
        
        this.updateVisuals();
    }
    
    get selected(): boolean {
        return this._selected;
    }
    
    set selected(value: boolean) {
        if (this._selected === value) return;
        
        this._selected = value;
        
        if (value) {
            const viewer = (window as any).viewer;
            let camera: THREE.Camera | undefined;
            let width = window.innerWidth;
            let height = window.innerHeight;
            
            if (viewer) {
                camera = viewer.scene.getActiveCamera();
                
                // Get viewport size from renderer
                const renderAreaSize = viewer.renderer.getSize(new THREE.Vector2());
                width = renderAreaSize.width;
                height = renderAreaSize.height;
            }
            
            if (camera) {
                this.createSpheres(camera, width, height);
            } else {
                this.createSpheres();
            }
            
            // Show measurement labels when selected
            this.createMeasurementLabels();
        } else {
            this.removeSpheres();
            this.removeMeasurementLabels();
        }
    }
    
    get elementType(): ElementType {
        return "line";
    }
    
    get displayedMeasurements(): MeasurementType[] {
        return this._measurementTypes ?? [];
    }

    set displayedMeasurements(value: MeasurementType[]) {
        this._measurementTypes = value;
        this._showDistances = this._measurementTypes.includes(MeasurementType.Distance3D) || 
                              this._measurementTypes.includes(MeasurementType.Distance2D);
        
        // Update visuals if selected to show the new measurement types
        if (this._selected) {
            this.updateVisuals();
            this.createMeasurementLabels();
        }
    }
    
    /**
     * Set points for the measurement
     */
    setPoints(points: THREE.Vector3[]) {
        this.points = points.map(v => ({ position: v }));
        this.updateVisuals();
    }
    
    /**
     * Set position of a specific marker
     */
    setPosition(index: number, position: THREE.Vector3) {
        if (index >= 0 && index < this.points.length) {
            this.points[index].position.copy(position);
            this.updateVisuals();
        }
    }
    
    /**
     * Calculate offsets for more efficient geometry storage
     */
    protected calculateOffsets(points: { position: THREE.Vector3 }[]) {
        if (!points || points.length === 0) return { origin: new THREE.Vector3(), offsetPoints: [] };
        
        const originX = Math.floor(points.reduce((min, p) => Math.min(min, p.position.x), Infinity));
        const originY = Math.floor(points.reduce((min, p) => Math.min(min, p.position.y), Infinity));
        const originZ = Math.floor(points.reduce((min, p) => Math.min(min, p.position.z), Infinity));
        const origin = new THREE.Vector3(originX, originY, originZ);
        
        // Create offset points
        const offsetPoints = points.map(p => new THREE.Vector3(
            p.position.x - origin.x,
            p.position.y - origin.y,
            p.position.z - origin.z
        ));

        return { origin, offsetPoints };
    }
    
    /**
     * Create selection spheres
     */
    protected createSpheres(camera?: THREE.Camera, clientWidth?: number, clientHeight?: number) { 
        // No points to create spheres for
        if (this.points.length === 0) return;
        
        // Calculate offsets for better precision
        const { origin, offsetPoints } = this.calculateOffsets(this.points);
        
        // Shared material for all spheres
        const material = new THREE.MeshBasicMaterial({ 
            opacity: 0.5, 
            color: 0xffffff,
            depthTest: false,
            depthWrite: false, 
            transparent: true 
        });
        
        // Create instanced mesh for all points
        const instancedMesh = new THREE.InstancedMesh(
            this.sphereGeometry,
            material,
            this.points.length
        );
        
        // Set position and scale for each instance
        const matrix = new THREE.Matrix4();
        offsetPoints.forEach((offsetPoint, index) => {            
            // Apply correct scale if camera is provided
            if (camera && clientWidth && clientHeight) {
                const worldPos = new THREE.Vector3().copy(offsetPoint).add(origin);
                const scale = this.calculateSphereScale(worldPos, camera, clientWidth, clientHeight);
                matrix.makeScale(scale, scale, scale);
            } else {
                matrix.makeScale(1, 1, 1);
            }
            
            matrix.setPosition(offsetPoint);
            instancedMesh.setMatrixAt(index, matrix);
        });
        
        // Store point indices for raycasting identification
        instancedMesh.instanceMatrix.needsUpdate = true;

        // Position the instanced mesh at the origin point
        instancedMesh.position.copy(origin);
        this.add(instancedMesh);
        this.spheres.push(instancedMesh);
    }
    
    /**
     * Create measurement labels for display
     */
    protected createMeasurementLabels() {
        // Remove existing labels first
        this.removeMeasurementLabels();
        
        // Don't create any labels if there are no measurement types selected or not enough points
        if (!TextSprite || this.points.length < 2 || this._measurementTypes.length === 0) return;
        
        const lastIndex = this.points.length - 1;
        
        for (let i = 0; i <= lastIndex; i++) {
            const index = i;
            const nextIndex = (i + 1 > lastIndex) ? 0 : i + 1;
            
            // Skip if this is the last segment and not closed
            if (index === lastIndex && !this.closed) continue;
            
            const point = this.points[index];
            const nextPoint = this.points[nextIndex];
            
            // Build the measurement text first
            const texts = [];
            
            if (this._measurementTypes.includes(MeasurementType.Distance2D)) {
                const distance2D = this.getDistance2D(point.position, nextPoint.position);
                if (distance2D) {
                    texts.push(`2D=${Utils.formatUnits(distance2D, this.unit)}`);
                }
            }
            
            if (this._measurementTypes.includes(MeasurementType.Distance3D)) {
                const distance3D = this.getDistance3D(point.position, nextPoint.position);
                if (distance3D) {
                    texts.push(`3D=${Utils.formatUnits(distance3D, this.unit)}`);
                }
            }
            
            if (this._measurementTypes.includes(MeasurementType.Height)) {
                const height = Math.abs(nextPoint.position.z - point.position.z);
                texts.push(`h=${Utils.formatUnits(height, this.unit)}`);
            }
            
            if (this._measurementTypes.includes(MeasurementType.Angle)) {
                const angle = this.getHorizontalAngle(point.position, nextPoint.position);
                texts.push(`\u2220=${radToDeg(angle).toFixed(1)}\u00B0`);
            }
            
            // Only create a label if there's something to show
            if (texts.length > 0) {
                // Create edge label
                const edgeLabel = new TextSprite("");
                edgeLabel.setBorderColor({r: 0, g: 0, b: 0, a: 1.0});
                edgeLabel.setBackgroundColor({r: 0, g: 0, b: 0, a: 1.0});
                edgeLabel.setTextColor({r: 255, g: 255, b: 255, a: 1.0});
                edgeLabel.material.depthTest = false;
                edgeLabel.material.opacity = 1.0;
                edgeLabel.visible = true;
                edgeLabel.fontsize = 16; // Match the original Measure class
                
                // Position label at midpoint of edge
                let center = new THREE.Vector3().add(point.position);
                center.add(nextPoint.position);
                center = center.multiplyScalar(0.5);
                edgeLabel.position.copy(center);
                
                edgeLabel.setText(texts.join("  "));
                this.add(edgeLabel);
                this.edgeLabels.push(edgeLabel);
            }
        }
        
        // Add area label if needed
        if ((this._measurementTypes.includes(MeasurementType.Area2D) || 
             this._measurementTypes.includes(MeasurementType.Area3D)) && 
            this.points.length >= 3 && this.closed) {
            
            // Build area text first
            const areaTexts = [];
            if (this._measurementTypes.includes(MeasurementType.Area2D)) {
                areaTexts.push(`\u00872D=${Utils.formatUnits(this.getArea2D(), this.unit+"\u00B2")}`);
            }
            if (this._measurementTypes.includes(MeasurementType.Area3D)) {
                areaTexts.push(`\u00873D=${Utils.formatUnits(this.getArea3D(), this.unit+"\u00B2")}`);
            }
            
            // Only create a label if there's area text to show
            if (areaTexts.length > 0) {
                const areaLabel = new TextSprite("");
                areaLabel.setTextColor({r: 140, g: 250, b: 140, a: 1.0});
                areaLabel.setBorderColor({r: 0, g: 0, b: 0, a: 1.0});
                areaLabel.setBackgroundColor({r: 0, g: 0, b: 0, a: 1.0});
                areaLabel.fontsize = 16; // Match the original Measure class
                areaLabel.material.depthTest = false;
                areaLabel.material.opacity = 1;
                
                // Position at centroid
                const centroid = new THREE.Vector3();
                this.points.forEach(point => centroid.add(point.position));
                centroid.divideScalar(this.points.length);
                areaLabel.position.copy(centroid);
                
                areaLabel.setText(areaTexts.join("  "));
                this.add(areaLabel);
                this.edgeLabels.push(areaLabel);
            }
        }
    }
    
    /**
     * Remove measurement labels
     */
    protected removeMeasurementLabels() {
        this.edgeLabels.forEach(label => this.remove(label));
        this.edgeLabels = [];
    }
    
    /**
     * Remove selection spheres
     */
    protected removeSpheres() {
        this.spheres.forEach(sphere => this.remove(sphere));
        this.spheres = [];
    }
    
    /**
     * Update all visual elements after changes
     */
    updateVisuals() {
        // Clear existing visuals except spheres
        this.children.forEach(child => {
            if (!(this.spheres.includes(child)) && !(this.edgeLabels.includes(child))) {
                this.remove(child);
            }
        });
        
        this.lines = [];
        
        // Recreate visuals
        this.createVisuals();
        
        // Update sphere positions if selected (don't recreate them)
        if (this._selected && this.spheres.length > 0) {
            this.updateSpherePositions();
        } else if (this._selected) {
            // Only create spheres if they don't exist yet
            this.createSpheres();
        }
        
        // Update measurement labels if selected
        if (this._selected) {
            this.createMeasurementLabels();
        }
    }

    /**
     * Update sphere positions without recreating them
     */
    updateSpherePositions() {
        if (this.spheres.length === 0 || this.points.length === 0) return;
        
        const instancedMesh = this.spheres[0] as THREE.InstancedMesh;
        if (!instancedMesh || !(instancedMesh instanceof THREE.InstancedMesh)) return;
        
        const { origin, offsetPoints } = this.calculateOffsets(this.points);
        instancedMesh.position.copy(origin);
        
        const matrix = new THREE.Matrix4();
        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        
        offsetPoints.forEach((offsetPoint, index) => {
            instancedMesh.getMatrixAt(index, matrix);
            matrix.decompose(position, quaternion, scale);
            matrix.compose(offsetPoint, quaternion, scale);
            instancedMesh.setMatrixAt(index, matrix);
        });
        
        instancedMesh.instanceMatrix.needsUpdate = true;
    }
    
    /**
     * Create visuals specific to the measurement type
     */
    createVisuals(): void {
        if (this.points.length < 2) return;
                
        // Calculate offsets for better precision
        const { origin, offsetPoints } = this.calculateOffsets(this.points);
        
        // Create path with all points
        const positions = [...offsetPoints];
        
        // Close the path if needed
        if (this.closed && this.points.length > 2) {
            positions.push(positions[0].clone());
        }
        
        // Create the line geometry
        const geometry = new THREE.BufferGeometry().setFromPoints(positions);
        const material = new THREE.LineBasicMaterial({ 
            color: this._color,
            linewidth: 2,
            depthTest: false
        });
        
        const line = new THREE.Line(geometry, material);
        line.position.copy(origin);
        this.add(line);
        this.lines.push(line);
    }
    
    /**
     * Get total distance of the measurement
     */
    getTotalDistance(): number {
        if (this.points.length < 2) return 0;
        
        let distance = 0;
        const outVector1 = new THREE.Vector3();
        const outVector2 = new THREE.Vector3();

        for (let i = 1; i < this.points.length; i++) {
            const prev = this.getPointInProjectCoordinateSystem(this.points[i - 1].position, outVector1);
            const curr = this.getPointInProjectCoordinateSystem(this.points[i].position, outVector2);
            distance += prev.distanceTo(curr);
        }

        if (this.closed && this.points.length > 1) {
            const first = this.getPointInProjectCoordinateSystem(this.points[0].position, outVector1);
            const last = this.getPointInProjectCoordinateSystem(this.points[this.points.length - 1].position, outVector2);
            distance += last.distanceTo(first);
        }

        return distance;
    }
    
    /**
     * Get 2D distance between two points
     */
    protected getDistance2D(pointA: THREE.Vector3, pointB: THREE.Vector3): number {        
        if (this.hasMapCoordinates && this.projectCoordinateSystem) {
            const p1 = proj4(this.mapCoordinateSystem, this.projectCoordinateSystem).forward([pointA.x, pointA.y, 0]);
            const p2 = proj4(this.mapCoordinateSystem, this.projectCoordinateSystem).forward([pointB.x, pointB.y, 0]);
            const v1 = new THREE.Vector3(p1[0], p1[1], 0);
            const v2 = new THREE.Vector3(p2[0], p2[1], 0);
            return v1.distanceTo(v2);
        } else if (this.hasMapCoordinates) {
            return Utils.distanceOnMap2d(pointA, pointB);
        } else {
            return Utils.distance2d(pointA, pointB);
        }
    }
    
    /**
     * Get 3D distance between two points
     */
    protected getDistance3D(pointA: THREE.Vector3, pointB: THREE.Vector3): number {        
        if (this.hasMapCoordinates && this.projectCoordinateSystem) {
            const p1 = proj4(this.mapCoordinateSystem, this.projectCoordinateSystem).forward([pointA.x, pointA.y, pointA.z]);
            const p2 = proj4(this.mapCoordinateSystem, this.projectCoordinateSystem).forward([pointB.x, pointB.y, pointB.z]);
            const v1 = new THREE.Vector3(p1[0], p1[1], p1[2]);
            const v2 = new THREE.Vector3(p2[0], p2[1], p2[2]);
            return v1.distanceTo(v2);
        } else if (this.hasMapCoordinates) {
            return Utils.distanceOnMap3d(pointA, pointB);
        } else {
            return Utils.distance3d(pointA, pointB);
        }
    }
    
    /**
     * Get area in 2D
     */
    getArea2D(): number {
        if (this.points.length < 3) return 0;
        
        let area = 0;
        let j = this.points.length - 1;
        const outVector1 = new THREE.Vector3();
        const outVector2 = new THREE.Vector3();
        
        for (let i = 0; i < this.points.length; i++) {
            const p1 = this.getPointInProjectCoordinateSystem(this.points[i].position, outVector1);
            const p2 = this.getPointInProjectCoordinateSystem(this.points[j].position, outVector2);
            area += (p2.x + p1.x) * (p1.y - p2.y);
            j = i;
        }

        return Math.abs(area / 2);
    }
    
    /**
     * Get area in 3D
     */
    getArea3D(): number {
        if (this.points.length < 3) return 0;
        
        const vertices = this.points.map(p => p.position);
        
        // Triangulate the shape
        const triangles = THREE.ShapeUtils.triangulateShape(vertices, []);
        
        // Calculate the area of each triangle and sum up
        const t = new THREE.Triangle();
        let totalArea = 0;
        const outVec = new THREE.Vector3();
        
        for (let i = 0; i < triangles.length; i++) {
            const triangle = triangles[i];
            
            let point = this.getPointInProjectCoordinateSystem(this.points[triangle[0]].position, outVec);
            t.a.x = point.x;
            t.a.y = point.y;
            t.a.z = point.z;
            
            point = this.getPointInProjectCoordinateSystem(this.points[triangle[1]].position, outVec);
            t.b.x = point.x;
            t.b.y = point.y;
            t.b.z = point.z;
            
            point = this.getPointInProjectCoordinateSystem(this.points[triangle[2]].position, outVec);
            t.c.x = point.x;
            t.c.y = point.y;
            t.c.z = point.z;
            
            totalArea += t.getArea();
        }
        
        return totalArea;
    }
    
    /**
     * Get horizontal angle
     */
    getHorizontalAngle(point1: THREE.Vector3, point2: THREE.Vector3): number {
        const v1 = new THREE.Vector3().subVectors(point2, point1).normalize();
        const v2 = new THREE.Vector3().set(v1.x, v1.y, 0).normalize();

        // Avoid errors if denominator is 0
        const denominator = Math.sqrt(v1.lengthSq() * v2.lengthSq());
        if (denominator === 0) {
            return 0;
        } else {
            return v2.angleTo(v1);
        }
    }
    
    /**
     * Get point in project coordinate system
     */
    getPointInProjectCoordinateSystem(point: THREE.Vector3, outVector: THREE.Vector3): THREE.Vector3 {
        if(this.hasMapCoordinates && this.projectCoordinateSystem){
            const converted = proj4(this.mapCoordinateSystem, this.projectCoordinateSystem).forward([point.x, point.y, point.z]);
            outVector.x = converted[0];
            outVector.y = converted[1];
            outVector.z = converted[2];
        } else {
            outVector.copy(point);
        }
        return outVector;
    }

    /**
     * Get point in target coordinate system (for UI)
     */
    getPointInTargetCoordinateSystem(index: number): THREE.Vector3 {
        const v = new THREE.Vector3();
        if(index >= 0 && index < this.points.length) {
            this.getPointInProjectCoordinateSystem(this.points[index].position, v);
        }
        return v;
    }

    /**
     * Update sphere scales based on camera distance to maintain consistent screen size
     */
    updateSphereScales(camera: THREE.Camera, clientWidth: number, clientHeight: number) {
        // Only update if selected and spheres exist
        if (!this._selected || this.spheres.length === 0) return;
        
        const instancedMesh = this.spheres[0] as THREE.InstancedMesh;
        if (!instancedMesh || !(instancedMesh instanceof THREE.InstancedMesh)) return;
    
        const { origin, offsetPoints } = this.calculateOffsets(this.points);
        const matrix = new THREE.Matrix4();
        const tempPosition = new THREE.Vector3();
    
        offsetPoints.forEach((offsetPoint, index) => {
            tempPosition.copy(offsetPoint).add(origin);
            const scale = this.calculateSphereScale(tempPosition, camera, clientWidth, clientHeight);
            
            matrix.makeScale(scale, scale, scale);
            matrix.setPosition(offsetPoint);
            instancedMesh.setMatrixAt(index, matrix);
        });
        
        instancedMesh.instanceMatrix.needsUpdate = true;
    }

    /**
     * Get index of a sphere from instanced mesh and intersection
     */
    public getSphereIndex(intersection: THREE.Intersection): number {
        if (!intersection) return -1;
        
        // Return the instance index from the intersection
        if (intersection.instanceId !== undefined) {
            return intersection.instanceId;
        }
        
        return -1;
    }

    private calculateSphereScale(position: THREE.Vector3, camera: THREE.Camera, clientWidth: number, clientHeight: number): number {
        const distance = camera.position.distanceTo(position);
        const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
        return (8 / pr);
    }
    
    /**
     * Update label scales based on camera distance to maintain consistent screen size
     */
    updateLabelScales(camera: THREE.Camera, clientWidth: number, clientHeight: number) {
        if (this.edgeLabels.length === 0) return;
        
        for (const label of this.edgeLabels) {
            const distance = camera.position.distanceTo(label.position);
            const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
            const scale = (70 / pr); // Use the same scale factor as in the original implementation
            
            label.scale.set(scale, scale, scale);
        }
    }
}